import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { fetchTokenConsumption, fetchAgents } from "../../store/agentSlice";
import {
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import styles from "./tokenConsumptionGraph.module.scss";

export default function TokenConsumptionGraph(): JSX.Element {
  const dispatch = useAppDispatch();
  const { tokenConsumption, tokenConsumptionLoading, tokenConsumptionError, data: agents } =
    useSelector((state: RootState) => state.agents);

  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  });

  const modelOptions = useMemo(() => [
    "GPT-5.1",
    "text-embedding-3-large",
  ], []);

  useEffect(() => {
    if (agents.length === 0) {
      dispatch(fetchAgents());
    }
  }, [agents.length, dispatch]);

  useEffect(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    dispatch(fetchTokenConsumption({
      month,
      year,
      type: "cumulative",
      category: selectedCategory || undefined,
      agentId: selectedAgentId ? parseInt(selectedAgentId) : undefined,
      modelName: selectedModel || undefined,
    }));
  }, [selectedMonth, selectedCategory, selectedAgentId, selectedModel, dispatch]);

  const tokenData = useMemo(() => {
    return tokenConsumption || [];
  }, [tokenConsumption]);

  const agentsWithoutTerms = useMemo(() => {
    return agents.filter((agent) => agent.id !== 501);
  }, [agents]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(agentsWithoutTerms.map((agent) => agent.category).filter(Boolean))
    ).sort();
    return uniqueCategories;
  }, [agentsWithoutTerms]);

  const filteredAgents = useMemo(() => {
    if (!selectedCategory) {
      return agentsWithoutTerms;
    }
    return agentsWithoutTerms.filter((agent) => agent.category === selectedCategory);
  }, [agentsWithoutTerms, selectedCategory]);

  const monthOptions = useMemo(() => {
    const options = [];
    const startDate = new Date(2025, 10, 1);
    const endDate = new Date(2026, 11, 1);

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setMonth(d.getMonth() + 1)
    ) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const monthName = d.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
      options.push({
        value: `${year}-${month}`,
        label: monthName,
      });
    }

    return options;
  }, []);

  return (
    <section className={styles.chartSection}>
      <div className={styles.chartHeader}>
        <TextField
          select
          label="Category"
          value={selectedCategory}
          onChange={(e) => {
            setSelectedCategory(e.target.value);
            setSelectedAgentId("");
          }}
          size="small"
          disabled={tokenConsumptionLoading}
          sx={{
            minWidth: 180,
            marginRight: 2,
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
            },
          }}
        >
          <MenuItem value="">
            <em>All Categories</em>
          </MenuItem>
          {categories.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Agent"
          value={selectedAgentId}
          onChange={(e) => setSelectedAgentId(e.target.value)}
          size="small"
          disabled={tokenConsumptionLoading}
          sx={{
            minWidth: 200,
            marginRight: 2,
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
            },
          }}
        >
          <MenuItem value="">
            <em>All Agents</em>
          </MenuItem>
          {filteredAgents
            .slice()
            .sort((a, b) => a.agent_name.localeCompare(b.agent_name))
            .map((agent) => (
              <MenuItem key={agent.id} value={String(agent.id)}>
                {agent.agent_name}
              </MenuItem>
            ))}
        </TextField>
        <TextField
          select
          label="Model"
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          size="small"
          disabled={tokenConsumptionLoading}
          sx={{
            minWidth: 220,
            marginRight: 2,
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
            },
          }}
        >
          <MenuItem value="">
            <em>All Models</em>
          </MenuItem>
          {modelOptions.map((model) => (
            <MenuItem key={model} value={model}>
              {model}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Select Month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          size="small"
          disabled={tokenConsumptionLoading}
          sx={{
            minWidth: 150,
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
            },
          }}
        >
          {monthOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </div>
      {tokenConsumptionLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 550,
          }}
        >
          <CircularProgress />
        </div>
      ) : tokenConsumptionError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {tokenConsumptionError}
        </Alert>
      ) : tokenData.length === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 550,
          }}
        >
          <Typography color="text.secondary">
            No token consumption data available for the selected month
          </Typography>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={550}>
          <LineChart
            data={tokenData}
            margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              interval={Math.floor(tokenData.length / 10)}
              height={80}
            />
            <YAxis
              domain={[0, "auto"]}
              label={{
                value: "Tokens (Million)",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip
              formatter={(value: string | number | undefined) => {
                if (value == null) return "";
                return `${Number(value).toFixed(2)}M`;
              }}
              labelFormatter={(label) => `Date: ${label}`}
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "10px",
              }}
              itemStyle={{
                color: "#00D2AA",
                padding: "2px 0",
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="tokens"
              stroke="#00D2AA"
              strokeWidth={2}
              name="Token Consumption (Cumulative)"
              dot={{ r: 5 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </section>
  );
}
